import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import CarouselCubit, { type ScrollProgressCallback } from "./CarouselCubit";
import { useBloc } from "@blac/react";

export interface CarouselNavApi {
  selectedIndex: number;
  totalSlides: number;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  isFirstSlide: boolean;
  isLastSlide: boolean;
  scrollSnaps: number[];
  scrollTo: (index: number) => void;
  scrollPrev: () => void;
  scrollNext: () => void;
  subscribeToProgress: (callback: ScrollProgressCallback) => () => void;
  getScrollProgress: () => number;
}

export type SlotRenderFn = (api: CarouselNavApi) => React.ReactNode;

export interface CarouselNavProps {
  id?: string;
  leftSlot?: SlotRenderFn;
  centerSlot?: SlotRenderFn;
  rightSlot?: SlotRenderFn;
  className?: string;
  style?: React.CSSProperties;
}

const NavWrapper = styled.nav`
  --nav-bg: rgba(0, 0, 0, 0.12);
  --nav-bg-active: rgba(0, 0, 0, 0.24);
  --nav-blur: 16px;
  --nav-pill-height: 2.5rem;
  --nav-pill-radius: 100px;
  --nav-dot-size: 0.625rem;
  --nav-dot-max-width: 2rem;

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  gap: 1rem;
`;

const pillBase = css`
  background-color: var(--nav-bg, rgba(0, 0, 0, 0.12));
  backdrop-filter: blur(var(--nav-blur, 16px));
  -webkit-backdrop-filter: blur(var(--nav-blur, 16px));
  border-radius: var(--nav-pill-radius, 100px);
  height: var(--nav-pill-height, 2.5rem);
`;

const NavSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: var(--nav-slot-justify, center);
  flex: var(--nav-slot-flex, 0 0 auto);
  min-width: 0;
`;

export const useCarousel = (id?: string): CarouselNavApi => {
  const [state, cubit] = useBloc(CarouselCubit, { id });

  return {
    selectedIndex: state.selectedIndex,
    totalSlides: state.scrollSnaps.length || state.totalSlides,
    canScrollPrev: state.canScrollPrev,
    canScrollNext: state.canScrollNext,
    isFirstSlide: cubit.isFirstSlide,
    isLastSlide: cubit.isLastSlide,
    scrollSnaps: state.scrollSnaps,
    scrollTo: cubit.scrollTo,
    scrollPrev: cubit.scrollPrev,
    scrollNext: cubit.scrollNext,
    subscribeToProgress: cubit.subscribeToProgress,
    getScrollProgress: cubit.getScrollProgress
  };
};

export const CarouselNav: React.FC<CarouselNavProps> = ({
  id,
  leftSlot,
  centerSlot,
  rightSlot,
  className,
  style
}) => {
  const api = useCarousel(id);

  return (
    <NavWrapper className={className} style={style}>
      <NavSlot
        style={{ "--nav-slot-justify": "flex-start" } as React.CSSProperties}
      >
        {leftSlot?.(api)}
      </NavSlot>
      <NavSlot
        style={
          {
            "--nav-slot-justify": "center",
            "--nav-slot-flex": "1"
          } as React.CSSProperties
        }
      >
        {centerSlot?.(api)}
      </NavSlot>
      <NavSlot
        style={{ "--nav-slot-justify": "flex-end" } as React.CSSProperties}
      >
        {rightSlot?.(api)}
      </NavSlot>
    </NavWrapper>
  );
};

const PillButton = styled.button`
  ${pillBase}
  -webkit-appearance: none;
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1rem;
  border: 0;
  margin: 0;
  color: var(--color-charcoal);
  touch-action: manipulation;
  cursor: pointer;
  transition:
    opacity 0.2s ease-in-out,
    background-color 0.15s ease-in-out;

  &:hover,
  &:active {
    background-color: var(--nav-bg-active, rgba(0, 0, 0, 0.24));
  }

  &:disabled,
  &[data-hidden="true"] {
    opacity: 0;
    pointer-events: none;
    cursor: default;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const TextPillButton = styled(PillButton)`
  font-size: 1rem;
  font-weight: 500;
`;

const DotsContainer = styled.div`
  ${pillBase}
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1rem;
`;

const Dot = styled.button`
  -webkit-appearance: none;
  appearance: none;
  background-color: #fff;
  border: 0;
  touch-action: manipulation;
  cursor: pointer;
  padding: 0;
  margin: 0;
  width: calc(
    var(--nav-dot-size, 0.625rem) +
      ((var(--nav-dot-max-width, 2rem) - var(--nav-dot-size, 0.625rem)) *
        var(--dot-active, 0))
  );
  height: var(--nav-dot-size, 0.625rem);
  border-radius: 1rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: var(--color-charcoal);
    opacity: calc(var(--dot-active, 0) * 1.5);
    border-radius: inherit;
  }
`;

export interface NavDotsProps {
  selectedIndex: number;
  totalSlides: number;
  scrollSnaps: number[];
  onDotClick?: (index: number) => void;
  subscribeToProgress: (callback: ScrollProgressCallback) => () => void;
  "aria-label"?: string;
}

export const getSlideProgress = (
  slideIndex: number,
  scrollProgress: number,
  scrollSnaps: number[]
): number => {
  if (scrollSnaps.length === 0) {
    return slideIndex === 0 ? 1 : 0;
  }
  if (scrollSnaps.length === 1) {
    return slideIndex === 0 ? 1 : 0;
  }

  const clamped = Math.max(0, Math.min(1, scrollProgress));

  for (let i = 0; i < scrollSnaps.length - 1; i++) {
    const start = scrollSnaps[i];
    const end = scrollSnaps[i + 1];

    if (clamped >= start && clamped <= end) {
      const progress = (clamped - start) / (end - start);
      if (i === slideIndex) {
        return 1 - progress;
      }
      if (i + 1 === slideIndex) {
        return progress;
      }
      return 0;
    }
  }

  if (clamped <= scrollSnaps[0]) {
    return slideIndex === 0 ? 1 : 0;
  }
  return slideIndex === scrollSnaps.length - 1 ? 1 : 0;
};

export const getAllSlideProgress = (
  scrollProgress: number,
  scrollSnaps: number[]
): number[] => {
  return scrollSnaps.map((_, index) =>
    getSlideProgress(index, scrollProgress, scrollSnaps)
  );
};

const calculateDotActiveAmounts = (
  scrollProgress: number,
  scrollSnaps: number[]
): number[] => {
  if (scrollSnaps.length === 0) {
    return [];
  }

  const activeAmounts: number[] = new Array<number>(scrollSnaps.length).fill(0);
  const clampedProgress = Math.max(0, Math.min(1, scrollProgress));

  if (scrollSnaps.length === 1) {
    activeAmounts[0] = 1;
    return activeAmounts;
  }

  for (let i = 0; i < scrollSnaps.length - 1; i++) {
    const startSnap = scrollSnaps[i];
    const endSnap = scrollSnaps[i + 1];

    if (clampedProgress >= startSnap && clampedProgress <= endSnap) {
      const segmentProgress =
        (clampedProgress - startSnap) / (endSnap - startSnap);
      activeAmounts[i] = 1 - segmentProgress;
      activeAmounts[i + 1] = segmentProgress;
      return activeAmounts;
    }
  }

  if (clampedProgress <= scrollSnaps[0]) {
    activeAmounts[0] = 1;
  } else {
    activeAmounts[scrollSnaps.length - 1] = 1;
  }

  return activeAmounts;
};

export const NavDots: React.FC<NavDotsProps> = ({
  selectedIndex,
  totalSlides,
  scrollSnaps,
  onDotClick,
  subscribeToProgress,
  "aria-label": ariaLabel = "Slide navigation"
}) => {
  const dotsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const scrollSnapsRef = useRef(scrollSnaps);
  scrollSnapsRef.current = scrollSnaps;

  useEffect(() => {
    return subscribeToProgress((progress) => {
      const snaps = scrollSnapsRef.current;
      const activeAmounts =
        snaps.length > 0
          ? calculateDotActiveAmounts(progress, snaps)
          : Array.from({ length: totalSlides }, (_, i) =>
              i === selectedIndex ? 1 : 0
            );

      dotsRef.current.forEach((dot, index) => {
        if (dot) {
          dot.style.setProperty(
            "--dot-active",
            String(activeAmounts[index] ?? 0)
          );
        }
      });
    });
  }, [subscribeToProgress, totalSlides, selectedIndex]);

  const handleKeyDown = (event: React.KeyboardEvent, currentIndex: number) => {
    let newIndex: number;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        newIndex = currentIndex < totalSlides - 1 ? currentIndex + 1 : 0;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : totalSlides - 1;
        break;
      case "Home":
        event.preventDefault();
        newIndex = 0;
        break;
      case "End":
        event.preventDefault();
        newIndex = totalSlides - 1;
        break;
      default:
        return;
    }

    dotsRef.current[newIndex]?.focus();
    onDotClick?.(newIndex);
  };

  return (
    <DotsContainer role="tablist" aria-label={ariaLabel}>
      {Array.from({ length: totalSlides }).map((_, index) => (
        <Dot
          key={index}
          ref={(el) => {
            dotsRef.current[index] = el;
          }}
          type="button"
          role="tab"
          onClick={() => onDotClick?.(index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          aria-label={`Go to slide ${index + 1} of ${totalSlides}`}
          aria-selected={index === selectedIndex}
          tabIndex={index === selectedIndex ? 0 : -1}
        />
      ))}
    </DotsContainer>
  );
};

export interface NavButtonProps {
  onClick: () => void;
  disabled?: boolean;
  hidden?: boolean;
  children?: React.ReactNode;
  "aria-label"?: string;
}

export const NavPrevButton: React.FC<NavButtonProps> = ({
  onClick,
  disabled,
  hidden,
  children,
  "aria-label": ariaLabel = "Go to previous slide"
}) => (
  <PillButton
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-hidden={hidden}
    aria-label={ariaLabel}
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M15 19l-7-7 7-7"
      />
    </svg>
    {children}
  </PillButton>
);

export const NavNextButton: React.FC<NavButtonProps> = ({
  onClick,
  disabled,
  hidden,
  children,
  "aria-label": ariaLabel = "Go to next slide"
}) => (
  <PillButton
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-hidden={hidden}
    aria-label={ariaLabel}
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
      style={{ transform: "scaleX(-1)" }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M15 19l-7-7 7-7"
      />
    </svg>
    {children}
  </PillButton>
);

export interface NavTextButtonProps {
  onClick: () => void;
  disabled?: boolean;
  children?: React.ReactNode;
  "aria-label"?: string;
}

export const NavTextButton: React.FC<NavTextButtonProps> = ({
  onClick,
  disabled,
  children,
  "aria-label": ariaLabel
}) => (
  <TextPillButton
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-hidden={disabled}
    aria-label={ariaLabel}
  >
    {children}
  </TextPillButton>
);

export default CarouselNav;
